/**
 * The Deal Score callout arm for the listing on screen (utils/deal-callout.ts).
 *
 * Asks the server only when the listing's own price analysis qualifies, so the
 * nine in ten listings without a callout cost no request. The server decides
 * the arm and records the view; this hook never shows anything it did not say.
 */
import { useEffect, useState } from 'react';
import { DEAL_CALLOUT_PREVIEW_PARAM, dealCalloutFor, previewArm } from '../../utils/deal-callout';
import {
  DealCalloutResponseSchema,
  type DealCallout,
  type DealCalloutArm,
} from '../../schemas/api/deal-callout';

export interface DealCalloutState {
  arm: DealCalloutArm;
  callout: DealCallout | null;
}

const NONE: DealCalloutState = { arm: 'none', callout: null };

export function useDealCallout(
  listing: { id?: string | number; price?: number | string; dataSourceId?: number; aiPriceAnalysis?: unknown } | null | undefined,
  enabled: boolean,
): DealCalloutState {
  const [state, setState] = useState<DealCalloutState>(NONE);
  const id = listing?.id;
  const qualifies = Boolean(enabled && id && dealCalloutFor(listing?.aiPriceAnalysis, listing?.price, listing?.dataSourceId));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a new listing starts with no callout until the server answers
    setState(NONE);
    if (!qualifies || !id) return;

    let live = true;
    const force = previewArm(new URLSearchParams(window.location.search).get(DEAL_CALLOUT_PREVIEW_PARAM));
    void (async () => {
      try {
        const res = await fetch('/api/deal-callout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ listingId: Number(id), ...(force ? { force } : {}) }),
        });
        if (!res.ok) return;
        // safeParse: a response from a newer or older deploy shows nothing rather than throwing.
        const parsed = DealCalloutResponseSchema.safeParse(await res.json());
        if (live && parsed.success && parsed.data.callout) {
          setState({ arm: parsed.data.arm, callout: parsed.data.callout });
        }
      } catch {
        // Network failure: no callout, which is also what the control arm sees.
      }
    })();
    return () => {
      live = false;
    };
  }, [id, qualifies]);

  return state;
}
